import { SHStack, SHTypography } from "@components/design-systems";
import SHSortableContext from "@components/design-systems/sh-sortable-context";
import {
  AnalysisSummaryDTO,
  ProductRecommendationDTO,
  RenderAnalysisSummaryItemProps,
} from "@models/platform-analysis/entities/steps/summary";
import { useTheme } from "@mui/material";
import { sortBySubProductOrder } from "@pages/platform-analysis-v2/_id/utils";
import { setProductRecommendations } from "@redux/slices/platform-analysis";
import { RootState, useAppDispatch } from "@redux/store";
import { debounce } from "lodash";
import { memo } from "react";
import { useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import { useUnmount } from "react-use";
import { ProductRecommendationDragItem } from "./drag-item";
export const SummaryProductRecommendations = memo(
  ({
    name,
    userType,
    isReview,
    disabled: sectionDisabled,
  }: RenderAnalysisSummaryItemProps) => {
    const dispatch = useAppDispatch();
    const { getValues, setValue } = useFormContext<AnalysisSummaryDTO>();
    const { palette } = useTheme();

    const storedShortlistedProductRecommendations = useSelector(
      (state: RootState) =>
        state.platformAnalysis?.platformAnalysis?.summary
          ?.productRecommendations,
    );
    const storedCurrentProductRecommendations = useSelector(
      (state: RootState) =>
        state.platformAnalysis?.internal?.summary?.productRecommendations,
    );
    const storedFeeAnalysisSelectedSubProducts = useSelector(
      (state: RootState) =>
        state.platformAnalysis?.platformAnalysis?.summary?.feeAnalysis
          ?.shortlisted?.subProducts,
    );

    const debounceDispatchUpdateOrders = debounce(
      (
        orders: Record<string, Partial<ProductRecommendationDTO>>,
        newData: ProductRecommendationDTO[],
      ) => {
        setValue("productRecommendations", newData);

        const sortedSubProducts = sortBySubProductOrder({
          data: storedFeeAnalysisSelectedSubProducts || [],
          productRecommendations: orders,
          getProductId: (item) => item.productId,
        });

        dispatch(
          setProductRecommendations({
            productRecommendations: orders,
            sortedSubProducts,
          }),
        );
      },
      300,
    );

    const handleOnDragEnd = (data: ProductRecommendationDTO[]) => {
      const currentFormData = getValues("productRecommendations") || [];
      const currentRecommendations = { ...storedCurrentProductRecommendations };
      const newData = data.map((item, index) => {
        currentRecommendations[item.productId] = {
          ...currentRecommendations[item.productId],
          order: index,
        };

        const existingItem = currentFormData.find(
          (i) => i.productId === item.productId,
        );

        if (!existingItem) return item;

        return {
          ...item,
          recommendationType: existingItem.recommendationType,
          additionalCustomLabel: existingItem.additionalCustomLabel,
        };
      });

      setValue("productRecommendations", newData, { shouldDirty: true });
      debounceDispatchUpdateOrders(currentRecommendations, newData);
    };

    useUnmount(() => {
      debounceDispatchUpdateOrders.cancel();
    });

    return (
      <SHStack gap={2}>
        <SHTypography disabled={sectionDisabled} variant="subtitle2">
          {name?.[userType]}
        </SHTypography>

        <SHStack
          gap={1}
          width={"100%"}
          direction={"column"}
          overflow={"scroll"}
          border={`1px solid ${palette.secondary[100]}`}
          padding={2}
          sx={{
            scrollbarWidth: "none",
            "& .SHSortableItem": {
              width: "calc(100%)",
            },
          }}
        >
          <SHSortableContext
            reOrderOnDragEnd
            items={storedShortlistedProductRecommendations || []}
            getItemId={(item) => item.productId}
            renderSortableItem={(item, index) => {
              if (!item || !item.productId) return <></>;

              const recommendationData =
                storedCurrentProductRecommendations[item.productId];

              if (!recommendationData) return <></>;

              return (
                <ProductRecommendationDragItem
                  key={item.productId}
                  index={index}
                  disabled={sectionDisabled}
                  recommendationData={recommendationData}
                  isReview={isReview}
                />
              );
            }}
            onDragEnd={(_, items) => handleOnDragEnd(items)}
          />
        </SHStack>
      </SHStack>
    );
  },
);
